import LoadingScreen from 'components/common/LoadingScreen';
import usePersistState from 'hooks/usePersistState';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import paths from 'routes/paths';
import { AbilityContext } from 'routes/permissions/Can';
import defineAbilityFor from 'routes/permissions/ability';
import { supabase, authHelpers } from '../lib/supabase';

/* -------------------------------------------------------------------------- */
/*                                  CONSTANTS                                */
/* -------------------------------------------------------------------------- */
export const ROLE_ORGANIZATION_ADMIN = 'Organisation Admin';
export const ROLE_BRANCH_STAFF = 'Staff';
export const ROLE_BRANCH_ADMIN = 'Branch Admin';

export const rolesMap = {
  organisationAdmin: {
    id: 'aa55dc52-ba98-4a23-9304-225e1d2708c4',
    name: ROLE_ORGANIZATION_ADMIN
  },
  staff: {
    id: '2c0a19f2-5819-4ddf-8c7d-d2daac1ff587',
    name: ROLE_BRANCH_STAFF
  },
  admin: {
    id: '95410e85-fc04-4c68-995c-e6204a54cf44',
    name: ROLE_BRANCH_ADMIN
  }
};

/* -------------------------------------------------------------------------- */
/*                                  CONTEXTS                                 */
/* -------------------------------------------------------------------------- */

const AuthContext = createContext({
  is_main: false,
  organization: null,
  user: null,
  selectedBranchId: null,
  selectedUserRole: null,
  isAdmin: true,
  isOrganizationAdmin: false,
  login: () => {},
  logout: () => {},
  registerOwner: () => {},
  isAuthenticated: false,
  isInitialized: false,
  setOrganization: () => {},
  selectBranch: () => {},
  selectAdminBranch: () => {},
  removeBranchSelection: () => {},
  ability: null,
  initializeUser: () => {}
});

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthContextProvider');
  }
  return context;
};

/* -------------------------------------------------------------------------- */
/*                             AUTH PROVIDER                                 */
/* -------------------------------------------------------------------------- */

export const AuthContextProvider = ({ children }) => {
  const [organization, setOrganization] = useState();
  const location = useLocation();
  const navigate = useNavigate();

  const [userRole, setUserRole] = usePersistState('userRole', {
    selectedBranchId: null,
    selectedUserRole: null,
    isAdmin: false,
    isOrganizationAdmin: false,
    permissions: null,
    role: null,
    is_main: false
  });

  const {
    selectedBranchId,
    selectedUserRole,
    isAdmin,
    isOrganizationAdmin,
    permissions,
    role,
    is_main,
    isAllBranches
  } = userRole;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize user from Supabase
  const initializeUser = async () => {
    try {
      const { user, error } = await authHelpers.getUser();

      if (error || !user) {
        setIsAuthenticated(false);
        setIsInitialized(true);
        setLoading(false);
        return {};
      }

      // Fetch user data from database with relations
      const { data: dbUser, error: dbError } = await supabase
        .from('users')
        .select(
          `
          *,
          organisation:organisation_id (
            id,
            organisation_name,
            organisation_email,
            logo,
            charity_number,
            city,
            country,
            address_1,
            address_2,
            post_code,
            currency_id,
            stripe_key
          ),
          users_roles (
            id,
            branch_id,
            organisation_id,
            role_id,
            is_active,
            permissions,
            role:role_id (
              name
            ),
            branch:branch_id (
              address,
              city,
              created_at,
              currency_id,
              id,
              location_id,
              logo,
              name,
              organisation_id,
              phone,
              is_main
            )
          )
        `
        )
        .eq('id', user.id)
        .maybeSingle();

      if (dbError || !dbUser) {
        // User exists in auth but not in the users table (e.g. donor-only account)
        // Don't logout — just mark as authenticated without admin data
        setIsAuthenticated(true);
        setIsInitialized(true);
        setLoading(false);
        return {};
      }

      setUserData(dbUser);
      const organization = dbUser.organisation;
      let branches = [];
      let userBranchRole = null;

      const isOrganizationAdmin = dbUser.users_roles.some(
        userRole =>
          userRole.role_id === rolesMap?.organisationAdmin?.id &&
          userRole.branch_id === null &&
          userRole.organisation_id === organization?.id
      );

      const isAdmin = dbUser.users_roles.some(
        userRole => userRole.role_id === rolesMap?.admin?.id
      );

      if (isOrganizationAdmin) {
        setUserRole(prev => ({
          ...prev,
          isAdmin: isOrganizationAdmin,
          isOrganizationAdmin,
          role: ROLE_ORGANIZATION_ADMIN
        }));

        // Fetch branches for org admin
        if (organization?.id) {
          const { data: branchData } = await supabase
            .from('branches')
            .select('*')
            .eq('organisation_id', organization.id);
          branches = branchData || [];
        }
      } else {
        // For branch admins/staff
        userBranchRole = dbUser.users_roles.find(
          role => role.is_active && role.branch_id
        );
        setUserRole(prev => ({
          ...prev,
          isAdmin,
          isOrganizationAdmin,
          role: isAdmin ? ROLE_BRANCH_ADMIN : ROLE_BRANCH_STAFF,
          permissions: dbUser.users_roles[0]?.permissions,
          selectedBranchId: userBranchRole?.branch_id,
          selectedUserRole: userBranchRole,
          is_main: userBranchRole?.branch?.is_main
        }));
      }

      setOrganization(organization);

      // Check email verification
      const matches = matchRoutes(
        [{ path: paths.verifyEmail }],
        location.pathname
      );

      if (organization && !dbUser.email_verified_at && !matches) {
        navigate(paths.verifyEmailAlert);
      }
      if (organization && dbUser.email_verified_at && matches) {
        navigate(paths.dashboard);
      }

      setIsAuthenticated(true);
      setIsInitialized(true);
      setLoading(false);

      return { organization, branches, userBranchRole };
    } catch (error) {
      console.error('Initialize user error:', error);
      setIsAuthenticated(false);
      setIsInitialized(true);
      setLoading(false);
      return {};
    }
  };

  // Listen to auth changes
  useEffect(() => {
    let isInitialLoad = true;

    // Initial session check
    const checkSession = async () => {
      await initializeUser();
      isInitialLoad = false;
    };

    checkSession();

    // Safety timeout - if auth hangs, unblock the app
    const timeout = setTimeout(() => {
      setIsInitialized(true);
      setLoading(false);
    }, 3000);

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip INITIAL_SESSION event to avoid double initialization
      if (event === 'INITIAL_SESSION') {
        return;
      }

      if (event === 'SIGNED_IN' && !isInitialLoad) {
        await initializeUser();
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setUserData(null);
        setOrganization(null);
      }
    });

    return () => {
      clearTimeout(timeout);
      subscription?.unsubscribe();
    };
  }, []);

  // Login
  const login = async ({ email, password }) => {
    try {
      const { data, error } = await authHelpers.signIn({ email, password });

      if (error) {
        toast.error(error.message);
        return;
      }

      // Initialize user will be called by onAuthStateChange
      const { organization, branches, userBranchRole } = await initializeUser();

      if (organization) {
        if (branches && branches.length > 0) {
          const firstBranch = branches.find(b => b.is_main) || branches[0];
          selectAdminBranch({
            selectedBranchId: firstBranch.id,
            is_main: firstBranch.is_main
          });
          navigate(paths.dashboard);
        } else if (userBranchRole) {
          selectBranch({
            userRole: userBranchRole,
            is_main: userBranchRole.branch?.is_main
          });
          navigate(paths.dashboard);
        } else {
          navigate(paths.dashboard);
        }
      } else {
        navigate(paths.registerBusinessInfo);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred during login');
    }
  };

  // Register owner
  const registerOwner = async ({
    title,
    firstName,
    lastName,
    phoneNumber,
    email,
    companyEmail,
    password
  }) => {
    try {
      const { data, error } = await authHelpers.signUp({
        email,
        password,
        firstName,
        lastName,
        title,
        phone: phoneNumber
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success(
        'Registration successful! Please check your email to verify your account.'
      );
      navigate(paths.login);
    } catch (error) {
      console.error('Register error:', error);
      toast.error('An error occurred during registration');
    }
  };

  // Logout
  const logout = async ({ sessionOut } = {}) => {
    await authHelpers.signOut();
    setIsAuthenticated(false);
    setUserData(null);
    setOrganization(null);

    if (sessionOut === true) {
      navigate(paths.sessionOut);
    } else {
      navigate(paths.login);
    }
  };

  // Branch selection helpers
  const selectAdminBranch = ({ selectedBranchId, is_main, isAllBranches }) => {
    setUserRole(prev => ({
      ...prev,
      selectedBranchId,
      is_main,
      isAllBranches: isAllBranches || false
    }));
  };

  const selectBranch = ({ userRole, is_main }) => {
    if (!userRole) {
      setUserRole({
        selectedBranchId: null,
        selectedUserRole: null,
        isAdmin: false,
        isOrganizationAdmin: false,
        permissions: null,
        role: null,
        is_main: is_main
      });
      return;
    }
    const isBranchAdmin = userRole.role_id === rolesMap.admin.id;
    setUserRole({
      selectedBranchId: userRole.branch_id,
      selectedUserRole: userRole,
      isAdmin: isBranchAdmin,
      isOrganizationAdmin: false,
      permissions: userRole.permissions,
      role: isBranchAdmin ? ROLE_BRANCH_ADMIN : ROLE_BRANCH_STAFF,
      is_main: userRole.is_main
    });
  };

  const removeBranchSelection = () => {
    setUserRole(prev => ({
      ...prev,
      selectedBranchId: null,
      selectedUserRole: null,
      isAdmin: isOrganizationAdmin,
      permissions: null
    }));
  };

  const ability = useMemo(() => {
    return defineAbilityFor(role, permissions);
  }, [permissions, role]);

  // Show loading screen briefly while auth initializes, with a timeout fallback
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        organization,
        user: userData ?? {},
        selectedBranchId,
        selectedUserRole,
        isAdmin,
        isOrganizationAdmin,
        is_main,
        isAllBranches,
        login,
        logout,
        registerOwner,
        isAuthenticated,
        isInitialized,
        setOrganization,
        selectBranch,
        selectAdminBranch,
        removeBranchSelection,
        ability,
        initializeUser
      }}
    >
      <AbilityContext.Provider value={ability}>
        {children}
      </AbilityContext.Provider>
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
